// Golden-fixture regression harness. Runs in the browser (where the pipeline
// actually runs): fetch each sample PDF + its golden JSON, run ingestFile,
// compare values. Open the wedge with ?selftest to execute.

import { ingestFile } from "./ingest";

interface GoldenItem { qty: number; model: string; serial: string; unitPrice: number }
interface Golden {
  kind: "text" | "scanned";
  ocr?: boolean;
  maker?: string; invoiceNo?: string; invoiceDate?: string; total?: number;
  items?: GoldenItem[]; pages?: number;
}

export interface FixtureReport { name: string; pass: boolean; diffs: string[] }

const FIXTURES = [
  "fixture-01-phonak-text",
  "fixture-02-resound-text",
  "fixture-03-phonak-scan",
];

async function fetchFirst(paths: string[]): Promise<Response> {
  for (const p of paths) {
    try {
      const r = await fetch(p);
      if (r.ok) return r;
    } catch { /* try the next base */ }
  }
  throw new Error("fixture not reachable: " + paths.join(" | "));
}

const bases = (file: string) => [`samples/${file}`, `../samples/${file}`];

export async function runSelftest(): Promise<FixtureReport[]> {
  const reports: FixtureReport[] = [];
  for (const name of FIXTURES) {
    const diffs: string[] = [];
    try {
      const [pdfRes, goldRes] = await Promise.all([
        fetchFirst(bases(`${name}.pdf`)),
        fetchFirst(bases(`${name}.golden.json`)),
      ]);
      const buf = await pdfRes.arrayBuffer();
      const golden = (await goldRes.json()) as Golden;
      const got = await ingestFile(buf);

      if (got.kind !== golden.kind) {
        diffs.push(`kind: got ${got.kind}, want ${golden.kind}` +
          (got.kind === "scanned" ? ` — note: ${got.note}` : "") +
          (got.kind === "unsupported" ? ` — reason: ${got.reason}` : ""));
      } else if (got.kind === "text" && golden.kind === "text") {
        if (golden.ocr && !got.ocr) diffs.push("expected an OCR-sourced read (got.ocr is false)");
        if (golden.maker && got.maker.value !== golden.maker) diffs.push(`maker: got "${got.maker.value}", want "${golden.maker}"`);
        if (golden.invoiceNo && got.invoiceNo.value !== golden.invoiceNo) diffs.push(`invoiceNo: got "${got.invoiceNo.value}", want "${golden.invoiceNo}"`);
        if (golden.invoiceDate && got.invoiceDate.value !== golden.invoiceDate) diffs.push(`invoiceDate: got "${got.invoiceDate.value}", want "${golden.invoiceDate}"`);
        if (golden.total !== undefined) {
          const t = got.total ? got.total.value : null;
          if (t === null || Math.abs(t - golden.total) > 0.005) diffs.push(`total: got ${t}, want ${golden.total}`);
        }
        const want = golden.items ?? [];
        if (got.items.length !== want.length) diffs.push(`items: got ${got.items.length}, want ${want.length}`);
        else want.forEach((w, i) => {
          const g = got.items[i];
          if (!g) { diffs.push(`item ${i}: missing`); return; }
          if (g.qty.value !== w.qty) diffs.push(`item ${i} qty: got ${g.qty.value}, want ${w.qty}`);
          if (g.model.value !== w.model) diffs.push(`item ${i} model: got "${g.model.value}", want "${w.model}"`);
          if (g.serial.value !== w.serial) diffs.push(`item ${i} serial: got "${g.serial.value}", want "${w.serial}"`);
          if (Math.abs(g.unitPrice.value - w.unitPrice) > 0.005) diffs.push(`item ${i} unitPrice: got ${g.unitPrice.value}, want ${w.unitPrice}`);
        });
        if (!got.arithmeticOk) diffs.push("arithmetic self-check failed");
      }
    } catch (e) {
      diffs.push(String(e));
    }
    reports.push({ name, pass: diffs.length === 0, diffs });
  }
  return reports;
}
